/**
 * Buy Credits Component
 * Flow: Select tier → (first purchase: sign contract) → confirm summary → enter password → Stripe
 *
 * Prices: the SERVER is the price authority. On open we ask `getCreditTiers`
 * for this client's quote (list price, any standing discount, what they pay).
 * If that lookup fails we fall back to the list prices below and say that any
 * discount is applied at checkout — a failed quote must never block a purchase.
 *
 * Promo codes: typed on the confirm step and checked by OUR server (never on
 * the Stripe page). "Apply" only previews the price; the code is held when the
 * client proceeds to payment. Standing discount vs code: the better one wins.
 */

import { useState, useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { functions, db } from '../../services/firebase';
import { useAuth } from '../../hooks/useAuth';
import { ContractSign } from './ContractSign';

// LIST prices — the fallback when the server quote cannot be loaded.
const CALL_TIERS = [
  { id: 'call_2500',  credits: 2500,  price: 375,   perCredit: '$0.15' },
  { id: 'call_5000',  credits: 5000,  price: 750,   perCredit: '$0.15' },
  { id: 'call_10000', credits: 10000, price: 1400,  perCredit: '$0.14' },
  { id: 'call_20000', credits: 20000, price: 2700,  perCredit: '$0.135' },
  { id: 'call_30000', credits: 30000, price: 3900,  perCredit: '$0.13' },
];

const CONTACT_TIERS = [
  { id: 'contact_150',  credits: 150,  price: 525,   perCredit: '$3.50' },
  { id: 'contact_300',  credits: 300,  price: 1050,  perCredit: '$3.50' },
  { id: 'contact_600',  credits: 600,  price: 2040,  perCredit: '$3.40' },
  { id: 'contact_1200', credits: 1200, price: 3960,  perCredit: '$3.30' },
  { id: 'contact_2400', credits: 2400, price: 7680,  perCredit: '$3.20' },
];

const ALL_TIERS = [...CALL_TIERS, ...CONTACT_TIERS, { id: 'platform', credits: 0, price: 500, perCredit: '' }];

/** What this client will actually pay for one item, from the server. */
interface Quote {
  listUsd: number;
  netUsd: number;
  netCents: number;
  discountPct: number;
}

/** The server's answer to "Apply" for ONE tier. */
type PromoResult =
  | {
      ok: true; code: string; tierId: string; percentOff: number;
      /** false = the account's standing discount is as good or better; the code is NOT used. */
      applied: boolean;
      listUsd: number; netUsd: number; netCents: number; discountPct: number;
    }
  | { ok: false; reason?: string; message: string };

interface TiersResponse {
  promo?: PromoResult;
  tiers: Array<{ id: string; priceUsd: number; netUsd?: number; netCents?: number; discountPct?: number }>;
  platformFeeUsd: number;
  platformFeeNetUsd?: number;
  platformFeeNetCents?: number;
  platformFeeDiscountPct?: number;
}

interface BuyCreditsProps {
  isOpen: boolean;
  onClose: () => void;
  billingType: string;
  platformPaidUntil?: number | null;
  /** Admin is in "Enter as Client": prices are a read-only preview, buying is blocked. */
  isImpersonating?: boolean;
  impersonatedClientId?: string | null;
}

const usd = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 })}`;

export function BuyCredits({
  isOpen, onClose, billingType, platformPaidUntil, isImpersonating = false, impersonatedClientId = null,
}: BuyCreditsProps) {
  const { user, reauthenticate } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'tiers' | 'contract' | 'confirm' | 'auth'>('tiers');
  const [pendingTier, setPendingTier] = useState<string | null>(null);
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [contractSigned, setContractSigned] = useState(false);
  const [clientName, setClientName] = useState('');
  // null = not loaded (or failed) → list prices are shown and the server decides at checkout.
  const [quotes, setQuotes] = useState<Record<string, Quote> | null>(null);
  const [promoInput, setPromoInput] = useState('');
  const [promoBusy, setPromoBusy] = useState(false);
  const [promo, setPromo] = useState<PromoResult | null>(null);
  // Only the LATEST "Apply" may set the price: a slow answer must never change it
  // after the client moved on, removed the code, or applied a different one.
  const applyRequest = useRef(0);

  // Listen for contract signed status
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(doc(db, 'clients', user.uid), snap => {
      if (snap.exists()) {
        setContractSigned(snap.data()?.contractSigned || false);
        setClientName(snap.data()?.name || '');
      }
    });
    return () => unsub();
  }, [user]);

  const loadQuotes = async (): Promise<Record<string, Quote> | null> => {
    try {
      const getTiers = httpsCallable<{ clientId?: string }, TiersResponse>(functions, 'getCreditTiers');
      const { data } = await getTiers(
        isImpersonating && impersonatedClientId ? { clientId: impersonatedClientId } : {},
      );
      const next: Record<string, Quote> = {};
      for (const t of data.tiers ?? []) {
        if (typeof t.netUsd === 'number' && typeof t.netCents === 'number') {
          next[t.id] = { listUsd: t.priceUsd, netUsd: t.netUsd, netCents: t.netCents, discountPct: t.discountPct ?? 0 };
        }
      }
      if (typeof data.platformFeeNetUsd === 'number' && typeof data.platformFeeNetCents === 'number') {
        next.platform = {
          listUsd: data.platformFeeUsd, netUsd: data.platformFeeNetUsd,
          netCents: data.platformFeeNetCents, discountPct: data.platformFeeDiscountPct ?? 0,
        };
      }
      const loaded = Object.keys(next).length > 0 ? next : null;
      setQuotes(loaded);
      return loaded;
    } catch (err) {
      console.error('[BuyCredits] quote lookup failed — showing list prices', err);
      setQuotes(null);
      return null;
    }
  };

  useEffect(() => {
    if (!isOpen || !user) return;
    loadQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user, isImpersonating, impersonatedClientId]);

  if (!isOpen) return null;

  const tiers = billingType === 'per_contact' ? CONTACT_TIERS : CALL_TIERS;
  const creditLabel = billingType === 'per_contact' ? 'Contact' : 'Call';
  const platformActive = platformPaidUntil && platformPaidUntil > Date.now();
  const platformDaysLeft = platformActive
    ? Math.ceil((platformPaidUntil - Date.now()) / (24 * 60 * 60 * 1000))
    : 0;

  const selectedTierInfo = ALL_TIERS.find(t => t.id === pendingTier);
  const quoteFor = (tierId: string): Quote | null => (quotes && quotes[tierId]) || null;
  // A code that was applied to THIS tier replaces the account quote on the confirm step.
  const promoForTier = promo && promo.ok && promo.tierId === pendingTier ? promo : null;
  const promoWon = !!promoForTier && promoForTier.applied;
  const selectedQuote: Quote | null = promoForTier
    ? { listUsd: promoForTier.listUsd, netUsd: promoForTier.netUsd, netCents: promoForTier.netCents, discountPct: promoForTier.discountPct }
    : (pendingTier ? quoteFor(pendingTier) : null);
  const platformQuote = quoteFor('platform');

  /** "$375" struck through + "$337.50" when discounted, else just the price. */
  const priceTag = (listPrice: number, q: Quote | null, size = '1rem') => {
    if (q && q.discountPct > 0) {
      return (
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textDecoration: 'line-through' }}>{usd(q.listUsd)}</div>
          <div style={{ fontWeight: 700, color: 'var(--gold)', fontSize: size }}>{usd(q.netUsd)}</div>
          <div style={{ fontSize: '0.7rem', color: '#10b981' }}>{q.discountPct}% off</div>
        </div>
      );
    }
    return <div style={{ fontWeight: 700, color: 'var(--gold)', fontSize: size }}>{usd(q ? q.netUsd : listPrice)}</div>;
  };

  const clearPromo = () => { applyRequest.current += 1; setPromo(null); setPromoInput(''); setPromoBusy(false); };

  /** Price preview only — nothing is held until the client proceeds to payment. */
  const handleApplyPromo = async () => {
    const typed = promoInput.trim();
    if (!typed || !pendingTier || promoBusy) return;
    const mine = ++applyRequest.current;
    setPromoBusy(true);
    setError(null);
    try {
      const getTiers = httpsCallable<{ clientId?: string; tierId: string; promoCode: string }, TiersResponse>(functions, 'getCreditTiers');
      const { data } = await getTiers({
        tierId: pendingTier, promoCode: typed,
        ...(isImpersonating && impersonatedClientId ? { clientId: impersonatedClientId } : {}),
      });
      if (mine !== applyRequest.current) return; // superseded — ignore this answer
      setPromo(data.promo ?? { ok: false, message: 'Promo codes are not available right now. You can still buy at the price shown.' });
    } catch (err) {
      if (mine !== applyRequest.current) return;
      console.error('[BuyCredits] promo check failed', err);
      setPromo({ ok: false, message: 'Could not check that code. Please try again. You can still buy at the price shown.' });
    } finally {
      if (mine === applyRequest.current) setPromoBusy(false);
    }
  };

  const handleSelectTier = (tierId: string) => {
    setPendingTier(tierId);
    clearPromo(); // a code is checked per item (a credits-only code cannot ride on the platform fee)
    setError(null);
    setAuthPassword('');
    setAuthError(null);

    if (!contractSigned && !isImpersonating) {
      setStep('contract');
    } else {
      setStep('confirm');
    }
  };

  const handleContractSigned = () => {
    setContractSigned(true);
    setStep('confirm');
  };

  const handleConfirmToAuth = () => {
    if (isImpersonating || promoBusy) return;
    // A code that was typed but never applied would be silently dropped and the
    // client would pay full price believing the code was in.
    if (promoInput.trim() && !promoForTier) {
      setError('Press Apply to use your promo code, or clear the box to continue without it.');
      return;
    }
    setStep('auth');
    setAuthPassword('');
    setAuthError(null);
  };

  const handlePurchase = async () => {
    if (!pendingTier || !authPassword || isImpersonating) return;
    setAuthError(null);
    setLoading(pendingTier);

    const success = await reauthenticate(authPassword);
    if (!success) {
      setAuthError('Incorrect password. Please try again.');
      setLoading(null);
      return;
    }

    try {
      const createCheckout = httpsCallable(functions, 'createCheckoutSession');
      // expectedNetCents is only COMPARED by the server (never used as the price).
      // Omitted when no quote loaded — the server still applies any discount.
      // With a winning promo code the expected price is the promo price, and the
      // code goes along so the server can hold it. A code that did not win is
      // NOT sent — it stays unused.
      const q = selectedQuote;
      const response = await createCheckout({
        tierId: pendingTier,
        ...(q ? { expectedNetCents: q.netCents } : {}),
        ...(promoWon && promoForTier ? { promoCode: promoForTier.code } : {}),
      });
      const data = response.data as { sessionUrl: string };
      if (data.sessionUrl) window.location.href = data.sessionUrl;
    } catch (err) {
      const reason = (err as { details?: { reason?: string } })?.details?.reason;
      if (typeof reason === 'string' && reason.startsWith('promo_')) {
        // The code could not be held (used up, expired, another checkout open…).
        // Nothing was charged. Back to the confirm step at the normal price.
        clearPromo();
        await loadQuotes();
        setError(`${err instanceof Error ? err.message : 'That promo code could not be used.'} Nothing was charged.`);
        setLoading(null);
        setAuthPassword('');
        setStep('confirm');
        return;
      }
      if (reason === 'price_changed') {
        clearPromo();
        // The discount changed after the quote was shown: show the new price
        // and let them confirm again. Nothing was charged.
        await loadQuotes();
        setError('The price for your account changed. Please review the new price below and confirm again.');
        setLoading(null);
        setAuthPassword('');
        setStep('confirm');
        return;
      }
      setError(err instanceof Error ? err.message : 'Unknown error');
      setLoading(null);
      setStep('tiers');
      setPendingTier(null);
      clearPromo();
    }
  };

  const handleClose = () => {
    setStep('tiers');
    setPendingTier(null);
    clearPromo();
    setAuthPassword('');
    setAuthError(null);
    setError(null);
    onClose();
  };

  // Contract signing overlay (first purchase only)
  if (step === 'contract') {
    return (
      <ContractSign
        isOpen={true}
        onClose={handleClose}
        onSigned={handleContractSigned}
        clientName={clientName}
      />
    );
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={handleClose}>
      <div style={{
        background: 'var(--dark-2)', borderRadius: '12px', padding: '32px',
        maxWidth: '560px', width: '90%', border: '1px solid var(--dark-3)',
        maxHeight: '90vh', overflowY: 'auto',
      }} onClick={e => e.stopPropagation()}>

        {isImpersonating && (
          <div style={{
            padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '0.85rem',
            background: 'rgba(200,168,78,0.1)', border: '1px solid var(--gold-dark)', color: 'var(--gold)',
          }}>
            Admin preview — these are the prices this client sees. Buying is off while you are in
            &ldquo;Enter as Client&rdquo;: a purchase here would be billed to your own admin account.
          </div>
        )}

        {/* Step: Tier Selection */}
        {step === 'tiers' && (
          <>
            <h2 style={{ margin: '0 0 16px', color: 'var(--white)' }}>Buy Credits</h2>

            {/* Platform Fee */}
            <div style={{
              padding: '14px 18px', borderRadius: '8px', marginBottom: '16px',
              background: 'var(--dark)', border: `1px solid ${platformActive ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--white)' }}>Platform Fee</div>
                  <div style={{ fontSize: '0.8rem', color: platformActive ? '#10b981' : '#dc2626', marginTop: '2px' }}>
                    {platformActive ? `Active - ${platformDaysLeft} days remaining` : 'Not active - required to send calls'}
                  </div>
                  {platformActive && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '2px' }}>
                      Renewing early adds 30 days to the days you have left.
                    </div>
                  )}
                </div>
                <button onClick={() => handleSelectTier('platform')} disabled={loading !== null}
                  className={platformActive ? 'btn btn-secondary' : 'btn btn-primary'}
                  style={{ padding: '8px 16px', fontSize: '0.85rem' }}>
                  {platformQuote && platformQuote.discountPct > 0 ? (
                    <>
                      <span style={{ textDecoration: 'line-through', opacity: 0.6, marginRight: '6px' }}>{usd(platformQuote.listUsd)}</span>
                      {usd(platformQuote.netUsd)} / month
                    </>
                  ) : `${usd(platformQuote ? platformQuote.netUsd : 500)} / month`}
                </button>
              </div>
            </div>

            {/* Credit Tiers */}
            <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: '12px' }}>
              {billingType === 'per_contact'
                ? 'Per Contact - charged for calls 30+ seconds where prospect was reached.'
                : 'Per Call - charged for every call submitted.'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              {tiers.map(tier => (
                <button key={tier.id} onClick={() => handleSelectTier(tier.id)} disabled={loading !== null}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '12px 16px', borderRadius: '8px', cursor: loading ? 'wait' : 'pointer',
                    background: 'var(--dark)', border: '1px solid var(--dark-3)', color: 'var(--white)',
                    fontFamily: 'inherit', fontSize: '0.9rem', transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={e => { if (!loading) e.currentTarget.style.borderColor = 'var(--gold)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--dark-3)'; }}>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 600 }}>{tier.credits.toLocaleString()} {creditLabel} Credits</div>
                    {/* "(list)" only when this client pays LESS than list — otherwise the
                        row must read exactly as it did before discounts existed. */}
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '2px' }}>
                      {tier.perCredit} per {creditLabel.toLowerCase()}{(quoteFor(tier.id)?.discountPct ?? 0) > 0 ? ' (list)' : ''}
                    </div>
                  </div>
                  {priceTag(tier.price, quoteFor(tier.id))}
                </button>
              ))}
            </div>

            {error && <p style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: '12px' }}>{error}</p>}
            {!quotes && (
              <p style={{ color: 'var(--gray-500)', fontSize: '0.75rem', marginBottom: '8px', textAlign: 'center' }}>
                List prices shown. Any discount on your account is applied at checkout.
              </p>
            )}
            <p style={{ color: 'var(--gray-500)', fontSize: '0.75rem', marginBottom: '16px', textAlign: 'center' }}>
              Secure payment via Stripe. You must accept the service agreement before payment.
            </p>
            <button onClick={handleClose} className="btn btn-secondary" style={{ width: '100%' }}>Cancel</button>
          </>
        )}

        {/* Step: Purchase Confirmation Summary */}
        {step === 'confirm' && selectedTierInfo && (
          <>
            <h2 style={{ margin: '0 0 16px', color: 'var(--white)' }}>Confirm Purchase</h2>
            <div style={{
              padding: '20px', borderRadius: '8px', marginBottom: '16px',
              background: 'var(--dark)', border: '1px solid var(--dark-3)', textAlign: 'center',
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--white)', marginBottom: '6px' }}>
                {pendingTier === 'platform' ? 'Platform Fee - 30 Days' : `${selectedTierInfo.credits.toLocaleString()} ${creditLabel} Credits`}
              </div>
              {selectedQuote && selectedQuote.discountPct > 0 ? (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ color: 'var(--gray-400)', fontSize: '0.9rem' }}>
                    List price <span style={{ textDecoration: 'line-through' }}>{usd(selectedQuote.listUsd)}</span>
                  </div>
                  <div style={{ color: '#10b981', fontSize: '0.9rem', margin: '2px 0 6px' }}>
                    {selectedQuote.discountPct}% {promoWon && promoForTier ? `promo code ${promoForTier.code}` : 'loyalty discount'} &minus;{usd(selectedQuote.listUsd - selectedQuote.netUsd)}
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--gold)' }}>
                    You pay {usd(selectedQuote.netUsd)}
                  </div>
                  {pendingTier !== 'platform' && (
                    <div style={{ color: 'var(--gray-400)', fontSize: '0.8rem', marginTop: '4px' }}>
                      You still receive the full {selectedTierInfo.credits.toLocaleString()} credits.
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--gold)', marginBottom: '12px' }}>
                  {usd(selectedQuote ? selectedQuote.netUsd : selectedTierInfo.price)}
                </div>
              )}
              {!selectedQuote && (
                <div style={{ color: 'var(--gray-500)', fontSize: '0.75rem', marginBottom: '8px' }}>
                  List price. Any discount on your account is applied at checkout.
                </div>
              )}
              <div style={{ color: '#dc2626', fontSize: '0.85rem', fontWeight: 600 }}>
                Credits are non-refundable once used.
              </div>
            </div>
            {/* Promo code — checked by our server; never typed on the Stripe page. */}
            <div style={{ marginBottom: '16px' }}>
              {promoWon && promoForTier ? (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
                  padding: '10px 12px', borderRadius: '6px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
                }}>
                  <span style={{ color: '#10b981', fontSize: '0.85rem' }}>
                    Code <strong>{promoForTier.code}</strong> applied &mdash; {promoForTier.percentOff}% off
                  </span>
                  <button type="button" onClick={clearPromo} style={{
                    background: 'none', border: 'none', color: 'var(--gray-400)', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline', fontFamily: 'inherit',
                  }}>Remove</button>
                </div>
              ) : (
                <>
                  <label htmlFor="promo-code" style={{ display: 'block', color: 'var(--gray-400)', fontSize: '0.8rem', marginBottom: '6px' }}>
                    Have a promo code?
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input id="promo-code" type="text" value={promoInput} maxLength={24} autoComplete="off" spellCheck={false}
                      onChange={e => { setPromoInput(e.target.value.toUpperCase()); if (promo) setPromo(null); }}
                      onKeyDown={e => { if (e.key === 'Enter') handleApplyPromo(); }}
                      placeholder="Enter code"
                      style={{
                        flex: 1, minWidth: 0, padding: '10px', background: 'var(--dark)', border: '1px solid var(--dark-3)',
                        borderRadius: '6px', color: 'var(--white)', fontFamily: 'inherit', letterSpacing: '0.05em',
                      }} />
                    <button type="button" onClick={handleApplyPromo} disabled={!promoInput.trim() || promoBusy} className="btn btn-secondary">
                      {promoBusy ? 'Checking...' : 'Apply'}
                    </button>
                  </div>
                  {promo && !promo.ok && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '6px 0 0' }}>{promo.message}</p>
                  )}
                  {promoForTier && !promoForTier.applied && (
                    <p style={{ color: 'var(--gray-400)', fontSize: '0.8rem', margin: '6px 0 0' }}>
                      Your account discount is already as good as this code ({promoForTier.percentOff}%), so the code was not used. You keep it for later.
                    </p>
                  )}
                </>
              )}
            </div>
            {error && <p style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: '12px' }}>{error}</p>}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => { setStep('tiers'); setPendingTier(null); setError(null); clearPromo(); }} className="btn btn-secondary" style={{ flex: 1 }}>Back</button>
              <button onClick={handleConfirmToAuth} disabled={isImpersonating || promoBusy} className="btn btn-primary" style={{ flex: 1 }}
                title={isImpersonating ? 'Exit client view to purchase' : undefined}>
                {isImpersonating ? 'Exit client view to purchase' : 'Confirm Purchase'}
              </button>
            </div>
          </>
        )}

        {/* Step: Password Re-authentication */}
        {step === 'auth' && (
          <>
            <h2 style={{ margin: '0 0 16px', color: 'var(--white)' }}>Verify Identity</h2>
            <p style={{ color: 'var(--gray-300)', fontSize: '0.85rem', marginBottom: '12px' }}>
              Enter your password to proceed to payment:
            </p>
            <input type="password" value={authPassword}
              onChange={e => setAuthPassword(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handlePurchase(); }}
              placeholder="Enter password" autoFocus
              style={{
                width: '100%', padding: '10px', marginBottom: '10px',
                background: 'var(--dark)', border: '1px solid var(--dark-3)',
                borderRadius: '6px', color: 'var(--white)', fontFamily: 'inherit',
              }} />
            {authError && <p style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: '8px' }}>{authError}</p>}
            {error && <p style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: '8px' }}>{error}</p>}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setStep('confirm')} className="btn btn-secondary" style={{ flex: 1 }}>Back</button>
              <button onClick={handlePurchase} disabled={!authPassword || loading !== null}
                className="btn btn-primary" style={{ flex: 1 }}>
                {loading ? 'Verifying...' : 'Proceed to Payment'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
